import React, { useMemo, useState } from 'react';
import { SectionHeader } from '../shared/SectionHeader';
import { MASIndirectGlobalOutputTable } from '../tables/MASIndirectGlobalOutputTable';
import { FormField } from '../shared/FormField';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { calculateGPPerUnit, calculateGrossProfit } from '../../bidAnalysisFormulas';
import classes from '../../bidAnalysisTemplate.module.scss';

interface MASIndirectGlobalOutputSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  formData: any;
  packSizeFormData?: Record<number, any>;
  gsmsHndlgType?: string;
  activePackSizeTab?: number | null;
  bidApprovedBy?: string | null;
  onFieldChange?: (field: string, value: any) => void;
  onFieldBlur?: (field: string, event?: any) => void;
  onSetActivePackSizeTab?: (tabIndex: number) => void;
  /** Latest VA market sales data month/year captured at bid creation (e.g. "01/25"). */
  vaDataAsOf?: string | null;
  /** Latest DOD market sales data month/year captured at bid creation (e.g. "01/25"). */
  dodDataAsOf?: string | null;
}

/**
 * Derive contractAnnualRevenue and contractAnnualGP from primary fields so that
 * MASIndirectGlobalOutputSection always shows up-to-date values, even when stored
 * fields are stale (e.g. between a keypress and blur, or right after Copy-for-all-years).
 */
function deriveRevenueAndGP(packData: any): { revenue: number; gp: number; usage: number } {
  const usage =
    parseFloat(packData.ncEstAnnualUsage || packData.targetAnnualUsage || '0') || 0;
  const bidPrice =
    parseFloat(packData.suggestedBidPrice || packData.suggested_bid_price || '0') || 0;

  // Distinguish between gsmsProfit being explicitly set (including 0) vs. missing/empty.
  // When missing, fall back to stored contractAnnualGP so copy-for-all-years values are preserved.
  const gsmsProfitRaw = packData.gsmsProfit ?? packData.gsms_profit;
  const hasExplicitProfit =
    gsmsProfitRaw !== undefined && gsmsProfitRaw !== null && gsmsProfitRaw !== '';
  const profit = hasExplicitProfit ? parseFloat(String(gsmsProfitRaw)) || 0 : null;

  if (usage > 0 && bidPrice > 0) {
    const revenue = usage * bidPrice;
    if (profit !== null) {
      const gp = profit !== 0 ? revenue * (profit / 100) : 0;
      return { revenue, gp, usage };
    }
    // gsmsProfit not present — use stored contractAnnualGP if available.
    const storedGP = parseFloat(packData.contractAnnualGP || '0') || 0;
    return { revenue, gp: storedGP, usage };
  }

  // Fallback to stored values when primary fields are unavailable
  const revenue = parseFloat(packData.contractAnnualRevenue || '0') || 0;
  const gp = parseFloat(packData.contractAnnualGP || '0') || 0;
  return { revenue, gp, usage };
}

export const MASIndirectGlobalOutputSection: React.FC<MASIndirectGlobalOutputSectionProps> = ({
  isExpanded,
  onToggle,
  formData,
  packSizeFormData,
  gsmsHndlgType,
  activePackSizeTab,
  bidApprovedBy,
  onFieldChange,
  onFieldBlur,
  onSetActivePackSizeTab,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);

  // Calculate sums across all pack sizes for the summary fields
  const calculatedValues = useMemo(() => {
    const rawPackSizes: any[] = packSizeFormData ? Object.values(packSizeFormData) : [formData];

    // Prefer the live formData for the single-pack-size / MAS case so that values
    // reflect the latest typed input (formData is updated on every keypress via
    // handleFieldChange, while packSizeFormData entries are only updated on blur).
    const allPackSizes =
      !packSizeFormData || Object.keys(packSizeFormData).length === 0
        ? [formData]
        : rawPackSizes;

    let totalContractAnnualRevenue = 0;
    let totalContractAnnualGP = 0;
    let totalAnnualUsage = 0;

    allPackSizes.forEach((packData: any) => {
      const { revenue, gp, usage } = deriveRevenueAndGP(packData);
      if (!isNaN(revenue)) totalContractAnnualRevenue += revenue;
      if (!isNaN(gp)) totalContractAnnualGP += gp;
      if (!isNaN(usage)) totalAnnualUsage += usage;
    });

    // Gross Profit = SUM(Contract Annual GP) / SUM(Contract Annual Revenue) * 100
    const grossProfit = calculateGrossProfit(totalContractAnnualRevenue, totalContractAnnualGP);

    // GP / Unit = SUM(Contract Annual GP) / SUM(Annual Usage)
    const gpPerUnit = calculateGPPerUnit(totalContractAnnualGP, totalAnnualUsage);

    return {
      totalContractAnnualRevenue,
      totalContractAnnualGP,
      totalAnnualUsage,
      grossProfit,
      gpPerUnit,
    };
  }, [formData, packSizeFormData]);

  const formatCurrency = (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value: number, decimals = 2): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.' + '0'.repeat(decimals);
    }
    return value.toFixed(decimals);
  };

  return (
    <div className={classes.section}>
      <SectionHeader title="Global Output Section" isExpanded={isExpanded} onToggle={onToggle}>
        {!!onFieldChange && (
          <Button
            label={isEditMode ? 'Done' : 'Edit'}
            icon={isEditMode ? 'pi pi-check' : 'pi pi-pencil'}
            size="small"
            severity={isEditMode ? 'success' : 'secondary'}
            onClick={e => {
              e.stopPropagation();
              setIsEditMode(prev => !prev);
            }}
            style={{
              marginRight: '0.5rem',
              ...(isEditMode
                ? {}
                : { backgroundColor: '#6B21A8', borderColor: '#6B21A8', color: '#fff' }),
            }}
          />
        )}
      </SectionHeader>

      {isExpanded && (
        <div className={classes.sectionContent}>
          <MASIndirectGlobalOutputTable
            formData={formData}
            packSizeFormData={packSizeFormData}
            gsmsHndlgType={gsmsHndlgType}
            activePackSizeTab={activePackSizeTab}
            bidApprovedBy={bidApprovedBy}
            isEditMode={isEditMode}
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
            onSetActivePackSizeTab={onSetActivePackSizeTab}
          />

          {/* Summary fields: Gross Profit % and GP/Unit */}
          <div
            className={classes.formGrid2}
            style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}
          >
            <FormField label="Gross Profit">
              <InputText
                value={formatNumber(calculatedValues.grossProfit, 2)}
                disabled
                readOnly
              />
            </FormField>

            <FormField label="GP / Unit">
              <InputText
                value={formatCurrency(calculatedValues.gpPerUnit)}
                disabled
                readOnly
              />
            </FormField>
          </div>
        </div>
      )}
    </div>
  );
};
